/**
 * The production calendar on the cheese plan page (spec 2026-10-18-production-calendar, §6; bsd-knowledge
 * v0.141): the days on which the plant makes fewer batches, or none, entered before the day by the people who
 * know.
 *
 * Ronald's Semler principle (SM-1 to SM-4, 18 October 2026): a block a person enters is planned at once, with no
 * approval step, and shows who entered it; only a block the system proposes (EAM or People) waits for a person to
 * accept or reject it. Before a block is saved the form shows what it does: the batches and milk it takes off per
 * day, the change to each week's milk to sell or buy, and whether that week's Wednesday sell proposal has gone.
 * After it is saved, To do shows it to the people it concerns, and anyone who reads the calendar may object. An
 * objection is no veto: the block stays as it is.
 *
 * "Block days" opens the form inline, under the button: platform-ui has no dialog, and an inline form is easier
 * on a phone. A block's days, batches, reason and title never change: "Change" opens the same form filled in and
 * saves a block that replaces it (PC-2). What the caller may do is the API's: `can_enter`, and per block
 * `can_accept` and `can_cancel`.
 */
import { Badge, Button, Radio, RadioGroup, Section, Select, SelectItem, Table, TextField } from "@depicomnlbv/platform-ui";
import { type FormEvent, useState } from "react";

import { errorCode, PlannerApiError } from "../api/client";
import {
  BLOCK_REASONS,
  BLOCK_REFUSALS,
  REASON_WORDS,
  SECTION_NAMES,
  useAcceptBlock,
  useBlockConsequence,
  useCancelBlock,
  useEnterBlock,
  useNewBlocks,
  useObjectToBlock,
  useProductionCalendar,
  useRejectBlock,
  type BlockConsequence,
  type BlockConsequenceBody,
  type BlockReason,
  type ProductionBlock,
  type ProductionBlockBody,
  type ProductionCalendar,
} from "../api/milkFlow";
import { formatLitres, formatShortDate, formatTime } from "../time";
import { batchesWord } from "./CalendarBadge";
import { Failure } from "./Failure";

const sentence = (text: string) => (/[.!?]$/.test(text) ? text : `${text}.`);

/** The API's own sentence for a 422, else the plain words for its code. */
function refusal(error: unknown): string {
  if (error instanceof PlannerApiError && error.status === 422 && error.detail) return sentence(error.detail);
  const code = errorCode(error);
  return (code && BLOCK_REFUSALS[code]) ?? "It was not saved. Try again; if it keeps failing, tell the administrator.";
}

const daysOf = (b: { first_day: string; last_day: string }) =>
  b.first_day === b.last_day ? formatShortDate(b.first_day) : `${formatShortDate(b.first_day)} to ${formatShortDate(b.last_day)}`;

const limitWord = (n: number) => (n === 0 ? "No production" : batchesWord(n));

const SOURCE_WORDS: Record<ProductionBlock["source"], string> = { manual: "", eam: "EAM", people: "People" };

/** The plant's own zone: the To do page has no calendar response to take it from. */
const PLANT_ZONE = "Europe/London";

/** "Ian Laing, 18 Oct 14:02", or for a system proposal "Proposed by EAM, 18 Oct 14:02". */
function enteredWords(b: ProductionBlock, timeZone: string): string {
  const at = `${formatShortDate(b.entered_at.slice(0, 10))} ${formatTime(b.entered_at, timeZone)}`;
  return b.entered_by ? `${b.entered_by}, ${at}` : `Proposed by ${SOURCE_WORDS[b.source]}, ${at}`;
}

/** Milk Flow's week figure in words: positive is milk to sell, negative milk to buy. */
const balanceWords = (litres: number) =>
  litres >= 0 ? `${formatLitres(Math.round(litres))} to sell` : `${formatLitres(Math.round(-litres))} to buy`;

interface Draft {
  first_day: string;
  last_day: string;
  batches: string;
  reason: BlockReason | null;
  title: string;
  eam_service_order: string;
  eam_project_code: string;
  note: string;
}

function draftOf(block: ProductionBlock | null, today: string): Draft {
  return {
    first_day: block && block.first_day > today ? block.first_day : today,
    last_day: block ? block.last_day : today,
    batches: block ? String(block.batches_allowed) : "0",
    reason: block?.reason ?? null,
    title: block?.title ?? "",
    eam_service_order: block?.eam_service_order ?? "",
    eam_project_code: block?.eam_project_code ?? "",
    note: block?.note ?? "",
  };
}

/** The block as the API takes it, or the words for the first thing missing before anything is sent. */
function bodyOf(draft: Draft, replaces: number | null): ProductionBlockBody | { error: string } {
  if (!draft.first_day || !draft.last_day) return { error: "Give the first and the last day." };
  if (draft.last_day < draft.first_day) return { error: BLOCK_REFUSALS.BAD_RANGE! };
  if (draft.reason === null) return { error: BLOCK_REFUSALS.BAD_REASON! };
  if (draft.title.trim().length < 3) return { error: BLOCK_REFUSALS.BAD_TITLE! };
  if (draft.reason === "other" && draft.note.trim() === "") return { error: BLOCK_REFUSALS.NOTE_REQUIRED! };
  const clean = (v: string) => (v.trim() === "" ? null : v.trim());
  return {
    first_day: draft.first_day,
    last_day: draft.last_day,
    batches_allowed: Number(draft.batches),
    reason: draft.reason,
    title: draft.title.trim(),
    note: draft.reason === "staffing" ? null : clean(draft.note),
    eam_service_order: clean(draft.eam_service_order),
    eam_project_code: clean(draft.eam_project_code),
    replaces,
  };
}

/** What the form's block does, asked as soon as its days are valid (SM-4 A). */
function consequenceBodyOf(draft: Draft, replaces: number | null): BlockConsequenceBody | null {
  if (!draft.first_day || !draft.last_day || draft.last_day < draft.first_day) return null;
  return { first_day: draft.first_day, last_day: draft.last_day, batches_allowed: Number(draft.batches), replaces };
}

function ConsequenceText({ c }: { c: BlockConsequence }) {
  if (c.unavailable.length > 0) {
    return (
      <p className="milk-flow-sub">
        Not worked out: {c.unavailable.map((s) => SECTION_NAMES[s] ?? s).join(", ")} could not be read.
      </p>
    );
  }
  const changed = c.days.filter((d) => d.batches_lost !== 0);
  const total =
    c.batches_lost > 0
      ? `Takes off ${batchesWord(c.batches_lost)} and frees ${formatLitres(Math.round(c.litres_freed))} of milk.`
      : c.batches_lost < 0
        ? `Gives back ${batchesWord(-c.batches_lost)} and takes ${formatLitres(Math.round(-c.litres_freed))} of milk.`
        : "Takes off no batch: the plan already has no more batches on these days.";
  return (
    <>
      <p>{total}</p>
      {changed.length > 0 ? (
        <div className="mf-scroll">
          <Table caption="What the block does per day" captionHidden className="milk-flow-table">
            <thead>
              <tr>
                <th scope="col">Day</th>
                <th scope="col">Batches</th>
                <th className="dpc-table__num" scope="col">
                  Milk freed
                </th>
              </tr>
            </thead>
            <tbody>
              {changed.map((d) => (
                <tr key={d.day}>
                  <th scope="row">{formatShortDate(d.day)}</th>
                  <td>
                    {d.batches_now} to {d.batches_then}
                    {d.source === "plant" ? (
                      <span className="dpc-table__sub">past the plan: the plant&rsquo;s batches at a full charge</span>
                    ) : null}
                  </td>
                  <td className="dpc-table__num">{formatLitres(Math.round(d.litres_freed))}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : null}
      {c.weeks
        .filter((w) => w.change_litres !== 0)
        .map((w) => (
          <p key={w.week}>
            Week of {formatShortDate(w.week)}: {balanceWords(w.balance_now_litres)} becomes{" "}
            {balanceWords(w.balance_then_litres)}
            {w.complete ? "" : " (not every day planned yet)"}.{" "}
            {w.proposal_gone
              ? "The Wednesday sell proposal for this week has gone: this milk is surplus to place."
              : "The Wednesday sell proposal for this week has not gone yet: it takes this milk into account."}
          </p>
        ))}
    </>
  );
}

function Consequence({ body }: { body: BlockConsequenceBody | null }) {
  const c = useBlockConsequence(body);
  if (body === null) return null;
  return (
    <section aria-label="What this block does" aria-live="polite" className="mf-consequence">
      <h5>What this block does</h5>
      {c.isPending ? <p className="milk-flow-sub">Working it out…</p> : null}
      {c.isError ? <p className="milk-flow-sub">{refusal(c.error)}</p> : null}
      {c.data ? <ConsequenceText c={c.data} /> : null}
    </section>
  );
}

function BlockForm({
  calendar,
  replaces,
  onDone,
}: {
  calendar: ProductionCalendar;
  replaces: ProductionBlock | null;
  onDone: (message: string) => void;
}) {
  const enter = useEnterBlock();
  const [draft, setDraft] = useState<Draft>(() => draftOf(replaces, calendar.today));
  const [error, setError] = useState<string | null>(null);
  const set = (change: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...change }));
    setError(null);
  };
  const [first, second] = calendar.draw_times;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (enter.isPending) return;
    const body = bodyOf(draft, replaces?.id ?? null);
    if ("error" in body) {
      setError(body.error);
      return;
    }
    enter.mutate(body, {
      onSuccess: (r) => onDone(replaces ? `Block ${replaces.id} replaced by block ${r.id}.` : `Block ${r.id} entered.`),
      onError: (e) => setError(refusal(e)),
    });
  };

  const heading = replaces ? `Change block ${replaces.id}` : "Block days";
  return (
    <form aria-label={heading} className="mf-block-form" noValidate onSubmit={submit}>
      <h4>{heading}</h4>
      <div className="mf-block-form__fields">
        <TextField label="First day" onChange={(first_day) => set({ first_day })} type="date" value={draft.first_day} />
        <TextField label="Last day" onChange={(last_day) => set({ last_day })} type="date" value={draft.last_day} />
      </div>
      <RadioGroup label="Batches still possible" onChange={(batches) => set({ batches })} value={draft.batches}>
        <Radio value="0">None</Radio>
        <Radio value="1">{first ? `1 batch (draw ${first})` : "1 batch"}</Radio>
        <Radio value="2">{first && second ? `2 batches (draws ${first}, ${second})` : "2 batches"}</Radio>
      </RadioGroup>
      <Consequence body={consequenceBodyOf(draft, replaces?.id ?? null)} />
      <Select
        label="Reason"
        onSelectionChange={(key) => set({ reason: key === null ? null : (String(key) as BlockReason) })}
        placeholder="Choose the reason"
        selectedKey={draft.reason}
      >
        {BLOCK_REASONS.map((r) => (
          <SelectItem id={r} key={r}>
            {REASON_WORDS[r]}
          </SelectItem>
        ))}
      </Select>
      <TextField label="What" onChange={(title) => set({ title })} value={draft.title} />
      <div className="mf-block-form__fields">
        <TextField
          description="Optional, WO-2026-0085"
          label="EAM work order"
          onChange={(eam_service_order) => set({ eam_service_order })}
          value={draft.eam_service_order}
        />
        <TextField
          description="Optional, 2026_0006"
          label="EAM project"
          onChange={(eam_project_code) => set({ eam_project_code })}
          value={draft.eam_project_code}
        />
      </div>
      {draft.reason === "staffing" ? null : (
        <TextField
          description={draft.reason === "other" ? "Required for this reason" : "Optional"}
          label="Note"
          onChange={(note) => set({ note })}
          value={draft.note}
        />
      )}
      <p className="milk-flow-sub">
        Saved, it is planned at once with your name on it. The dispatcher, trading and the cheesemakers see it on To
        do.
      </p>
      <div className="mf-plan-actions">
        <Button isPending={enter.isPending} pendingLabel="Saving" type="submit">
          Save
        </Button>
        <Button onPress={() => onDone("")} variant="subtle">
          Close
        </Button>
      </div>
      {error ? (
        <p className="mf-plan-error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

type TextAction = "cancel" | "reject" | "object";

interface TextWords {
  label: (id: number) => string;
  submit: string;
  pending: string;
  done: (id: number) => string;
  tooShort: string;
}

const TEXT_ACTIONS: Record<TextAction, TextWords> = {
  cancel: {
    label: (id) => `Why block ${id} is cancelled`,
    submit: "Cancel the block",
    pending: "Cancelling",
    done: (id) => `Block ${id} cancelled.`,
    tooShort: BLOCK_REFUSALS.CANCEL_REASON_REQUIRED!,
  },
  reject: {
    label: (id) => `Why the proposed block ${id} is rejected`,
    submit: "Reject the proposal",
    pending: "Rejecting",
    done: (id) => `Proposed block ${id} rejected.`,
    tooShort: BLOCK_REFUSALS.CANCEL_REASON_REQUIRED!,
  },
  object: {
    label: (id) => `Your objection to block ${id}`,
    submit: "Send the objection",
    pending: "Sending",
    done: (id) => `Objection to block ${id} recorded. The block stays as it is.`,
    tooShort: BLOCK_REFUSALS.BAD_OBJECTION!,
  },
};

/** One short text about a block: why it is cancelled or rejected, or an objection (no veto). */
function TextForm({ action, block, onDone }: { action: TextAction; block: ProductionBlock; onDone: (message: string) => void }) {
  const cancel = useCancelBlock();
  const reject = useRejectBlock();
  const object = useObjectToBlock();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const words = TEXT_ACTIONS[action];
  const pending = cancel.isPending || reject.isPending || object.isPending;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (pending) return;
    const value = text.trim();
    if (value.length < 3) {
      setError(words.tooShort);
      return;
    }
    const handlers = { onSuccess: () => onDone(words.done(block.id)), onError: (e: unknown) => setError(refusal(e)) };
    if (action === "cancel") cancel.mutate({ id: block.id, reason: value }, handlers);
    else if (action === "reject") reject.mutate({ id: block.id, reason: value }, handlers);
    else object.mutate({ id: block.id, objection: value }, handlers);
  };
  return (
    <form aria-label={words.submit} className="mf-block-form" noValidate onSubmit={submit}>
      <TextField
        errorMessage={error ?? undefined}
        isInvalid={Boolean(error)}
        label={words.label(block.id)}
        onChange={(v) => {
          setText(v);
          setError(null);
        }}
        value={text}
      />
      <div className="mf-plan-actions">
        <Button isPending={pending} pendingLabel={words.pending} type="submit" variant="secondary">
          {words.submit}
        </Button>
        <Button onPress={() => onDone("")} variant="subtle">
          Close
        </Button>
      </div>
    </form>
  );
}

function Objections({ block, timeZone }: { block: ProductionBlock; timeZone: string }) {
  if (block.objections.length === 0) return null;
  return (
    <ul className="mf-objections">
      {block.objections.map((o) => (
        <li key={o.id}>
          Objection, {o.raised_by ?? "someone"}, {formatShortDate(o.raised_at.slice(0, 10))}{" "}
          {formatTime(o.raised_at, timeZone)}: {o.objection}
        </li>
      ))}
    </ul>
  );
}

type Open = { kind: "new" } | { kind: "change"; block: ProductionBlock } | { kind: TextAction; block: ProductionBlock } | null;

/** The buttons for one block: accept or reject a proposal, change or cancel a planned block, and object. */
function BlockActions({
  block,
  canChange,
  onOpen,
  onMessage,
}: {
  block: ProductionBlock;
  canChange: boolean;
  onOpen: (open: Open) => void;
  onMessage: (message: string) => void;
}) {
  const accept = useAcceptBlock();
  return (
    <div className="mf-blocks__actions">
      {block.can_accept ? (
        <>
          <Button
            aria-label={`Accept block ${block.id}`}
            isPending={accept.isPending && accept.variables === block.id}
            onPress={() =>
              accept.mutate(block.id, {
                onSuccess: () => onMessage(`Block ${block.id} accepted: it is planned.`),
                onError: (e) => onMessage(refusal(e)),
              })
            }
            pendingLabel="Accepting"
            variant="secondary"
          >
            Accept
          </Button>
          <Button aria-label={`Reject block ${block.id}`} onPress={() => onOpen({ kind: "reject", block })} variant="subtle">
            Reject
          </Button>
        </>
      ) : null}
      {canChange && block.status === "planned" ? (
        <Button aria-label={`Change block ${block.id}`} onPress={() => onOpen({ kind: "change", block })} variant="subtle">
          Change
        </Button>
      ) : null}
      {block.can_cancel && block.status === "planned" ? (
        <Button aria-label={`Cancel block ${block.id}`} onPress={() => onOpen({ kind: "cancel", block })} variant="subtle">
          Cancel
        </Button>
      ) : null}
      <Button aria-label={`Object to block ${block.id}`} onPress={() => onOpen({ kind: "object", block })} variant="subtle">
        Object
      </Button>
    </div>
  );
}

function StatusBadge({ block }: { block: ProductionBlock }) {
  return block.status === "planned" ? <Badge tone="done">Planned</Badge> : <Badge tone="medium">Proposed</Badge>;
}

function BlocksTable({
  calendar,
  blocks,
  onOpen,
  onMessage,
}: {
  calendar: ProductionCalendar;
  blocks: ProductionBlock[];
  onOpen: (open: Open) => void;
  onMessage: (message: string) => void;
}) {
  return (
    <div className="mf-scroll">
      <Table caption="Blocks ahead, the earliest first" captionHidden className="milk-flow-table mf-blocks">
        <thead>
          <tr>
            <th scope="col">Days</th>
            <th scope="col">Batches</th>
            <th scope="col">Reason</th>
            <th scope="col">What</th>
            <th scope="col">EAM</th>
            <th scope="col">Status</th>
            <th scope="col">Entered</th>
            <th scope="col">
              <span className="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {blocks.map((b) => (
            <tr key={b.id}>
              <th scope="row">{daysOf(b)}</th>
              <td>{limitWord(b.batches_allowed)}</td>
              <td>{REASON_WORDS[b.reason]}</td>
              <td>
                {b.title}
                {b.note ? <span className="dpc-table__sub">{b.note}</span> : null}
                <Objections block={b} timeZone={calendar.time_zone} />
              </td>
              <td>{[b.eam_service_order, b.eam_project_code].filter(Boolean).join(" · ") || <span className="nothing">—</span>}</td>
              <td>
                <StatusBadge block={b} />
              </td>
              <td>
                {enteredWords(b, calendar.time_zone)}
                {b.accepted_by ? <span className="dpc-table__sub">Accepted by {b.accepted_by}</span> : null}
              </td>
              <td>
                <BlockActions block={b} canChange={calendar.can_enter} onMessage={onMessage} onOpen={onOpen} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function OpenForm({ calendar, open, onDone }: { calendar: ProductionCalendar | null; open: Open; onDone: (m: string) => void }) {
  if (open === null) return null;
  if (open.kind === "new" || open.kind === "change") {
    if (calendar === null) return null;
    return (
      <BlockForm
        calendar={calendar}
        key={open.kind === "change" ? open.block.id : "new"}
        onDone={onDone}
        replaces={open.kind === "change" ? open.block : null}
      />
    );
  }
  return <TextForm action={open.kind} block={open.block} key={`${open.kind}-${open.block.id}`} onDone={onDone} />;
}

/** The form that is open and the last thing done, shared by the calendar and To do. */
function useOpenForm() {
  const [open, setOpen] = useState<Open>(null);
  const [message, setMessage] = useState("");
  return {
    open,
    message,
    setMessage,
    openForm: (o: Open) => {
      setOpen(o);
      setMessage("");
    },
    done: (m: string) => {
      setOpen(null);
      setMessage(m);
    },
  };
}

function Message({ text }: { text: string }) {
  return text ? (
    <p className="milk-flow-sub" role="status">
      {text}
    </p>
  ) : null;
}

export function ProductionCalendarSection() {
  const calendar = useProductionCalendar();
  const form = useOpenForm();

  if (calendar.isPending) return <Section loading title="Production calendar" />;
  if (calendar.isError) {
    return (
      <Section title="Production calendar">
        <Failure context={{ screen: "Block days" }} error={calendar.error} />
      </Section>
    );
  }
  const c = calendar.data;
  const active = c.blocks.filter((b) => b.status !== "cancelled" && b.last_day >= c.today);
  const editing = form.open?.kind === "new" || form.open?.kind === "change";
  return (
    <>
      <Section title="Production calendar">
        {c.unavailable.length > 0 ? (
          <p className="milk-flow-notice" role="status">
            {c.unavailable.map((s) => SECTION_NAMES[s] ?? s).join(", ")}: not available yet.
          </p>
        ) : null}
        {c.can_enter && !editing ? (
          <div className="mf-plan-actions">
            <Button onPress={() => form.openForm({ kind: "new" })} variant="secondary">
              Block days
            </Button>
          </div>
        ) : null}
        <OpenForm calendar={c} onDone={form.done} open={form.open} />
        <Message text={form.message} />
      </Section>
      <Section
        aside="next 8 weeks"
        empty={active.length === 0 ? "No production is blocked in the next 8 weeks." : undefined}
        flush
        title="Blocks ahead"
      >
        {active.length > 0 ? (
          <BlocksTable blocks={active} calendar={c} onMessage={form.setMessage} onOpen={form.openForm} />
        ) : null}
      </Section>
    </>
  );
}

/**
 * On To do (SM-4 A): each block entered in the last days whose days are still to come, with who entered it and
 * the people it concerns, and each proposal waiting for a person. Anyone may object; a proposal is accepted or
 * rejected by the people who write the calendar.
 */
export function NewBlocksSection() {
  const blocks = useNewBlocks();
  const form = useOpenForm();
  // A caller who may not read Milk Flow sees none, like the alerts.
  if (blocks.isPending || blocks.isError || blocks.data.lines.length === 0) return null;
  return (
    <Section flush title="New on the production calendar">
      <div className="mf-scroll">
        <Table caption="Blocks entered lately and proposals waiting" captionHidden className="milk-flow-table mf-blocks">
          <thead>
            <tr>
              <th scope="col">Days</th>
              <th scope="col">Batches</th>
              <th scope="col">What</th>
              <th scope="col">Status</th>
              <th scope="col">Entered</th>
              <th scope="col">For</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {blocks.data.lines.map(({ block: b, people }) => (
              <tr key={b.id}>
                <th scope="row">{daysOf(b)}</th>
                <td>{limitWord(b.batches_allowed)}</td>
                <td>
                  {b.title}
                  <span className="dpc-table__sub">{REASON_WORDS[b.reason]}</span>
                  <Objections block={b} timeZone={PLANT_ZONE} />
                </td>
                <td>
                  <StatusBadge block={b} />
                </td>
                <td>{enteredWords(b, PLANT_ZONE)}</td>
                <td>{people.length > 0 ? people.join(", ") : <span className="nothing">—</span>}</td>
                <td>
                  <BlockActions block={b} canChange={false} onMessage={form.setMessage} onOpen={form.openForm} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <OpenForm calendar={null} onDone={form.done} open={form.open} />
      <Message text={form.message} />
    </Section>
  );
}
